/* RichCareerTimeline — Phase 1 (item #3)
 * ─────────────────────────────────────────────────────────────────────
 * Vertical, cinematic timeline of career milestones. Each milestone
 * can carry a title, date, description, photos, videos, articles,
 * awards list, and a stats object. Reads from `athlete.career_milestones`
 * where each item is `{id, date, title, description, category, photos,
 * videos, articles, awards, stats, pinned}`.
 *
 * Ordering: pinned first, then chronological (most recent last so the
 * timeline reads bottom-up like a career arc).
 */
import { Trophy, GraduationCap, Star, Flag, Zap, Shield, Sparkles } from 'lucide-react';

const CATEGORY_META = {
  award:         { icon: Trophy,       color: '#fbbf24', label: 'Award' },
  championship:  { icon: Shield,       color: '#f97316', label: 'Championship' },
  draft:         { icon: Zap,          color: '#38bdf8', label: 'Draft' },
  commitment:    { icon: GraduationCap,color: '#a78bfa', label: 'Commitment' },
  debut:         { icon: Star,         color: '#f472b6', label: 'Debut' },
  transfer:      { icon: Flag,         color: '#22d3ee', label: 'Transfer' },
  custom:        { icon: Sparkles,     color: '#34d399', label: '' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  // Accept "2018-12", "2018-12-15", or bare "2018"
  const s = String(iso).trim();
  if (/^\d{4}$/.test(s)) return s;
  try {
    const d = new Date(s.length === 7 ? `${s}-01` : s);
    if (isNaN(d.getTime())) return s;
    // Show month + year for month-only; full date otherwise.
    const showDay = /^\d{4}-\d{2}-\d{2}/.test(s);
    return d.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', ...(showDay ? { day: 'numeric' } : {}),
    });
  } catch { return s; }
};

// iter193 · Normalize milestones written via the athlete-authored
// section drawer (`{year, label, detail, team, current}`) to the
// rich schema this component was originally built for
// (`{date, title, description, pinned, ...}`). Without this,
// athlete-created milestones rendered as empty green "MILESTONE"
// pills with no title/year in the Career tab.
const normalizeMilestone = (m = {}) => {
  const hasRich = m.title || m.date || m.description;
  if (hasRich) return m;
  const title = m.label || m.title || '';
  const year = m.year != null ? String(m.year) : '';
  // Prefix the team into the description when present so the vertical
  // card still surfaces it (no dedicated team field in the rich shape).
  const desc = m.detail || m.description || '';
  const description = m.team ? (desc ? `${m.team} · ${desc}` : m.team) : desc;
  return {
    ...m,
    title,
    date: year, // fmtDate handles bare "2026"
    description,
    pinned: !!m.current || !!m.pinned,
  };
};

const orderMilestones = (list = []) => {
  const withMeta = list.map((raw, i) => {
    const m = normalizeMilestone(raw);
    return { ...m, __i: i, __ts: (m.date || '').padEnd(10, '0') };
  });
  const pinned = withMeta.filter((m) => m.pinned).sort((a, b) => (a.__ts < b.__ts ? -1 : 1));
  const rest = withMeta.filter((m) => !m.pinned).sort((a, b) => (a.__ts < b.__ts ? -1 : 1));
  return [...pinned, ...rest];
};

const MilestoneCard = ({ milestone }) => {
  const meta = CATEGORY_META[milestone.category] || CATEGORY_META.custom;
  const Icon = meta.icon;
  const photos = milestone.photos || [];
  const videos = milestone.videos || [];
  const articles = milestone.articles || [];
  const awards = milestone.awards || [];
  const stats = milestone.stats || null;
  return (
    <div
      className="relative pl-10 sm:pl-14 pb-8 group"
      data-testid={`career-milestone-${milestone.id || milestone.title?.slice(0, 20)}`}
    >
      {/* Vertical rail dot */}
      <div
        className="absolute left-[10px] sm:left-[18px] top-1.5 w-3.5 h-3.5 rounded-full ring-4 ring-black z-10"
        style={{ background: meta.color, boxShadow: `0 0 20px ${meta.color}55` }}
      />
      {milestone.pinned && (
        <span
          className="absolute left-[9px] sm:left-[17px] top-1 w-4 h-4 rounded-full animate-ping opacity-40"
          style={{ background: meta.color }}
          aria-hidden
        />
      )}

      {/* Card */}
      <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-4 sm:p-5 backdrop-blur-md hover:bg-white/8 transition">
        <div className="flex items-start justify-between gap-3 mb-1.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-widest font-bold"
                style={{ background: `${meta.color}22`, color: meta.color }}
              >
                <Icon className="w-3 h-3" />
                {meta.label || 'Milestone'}
              </span>
              {milestone.date && (
                <span className="text-[10px] uppercase tracking-widest text-gray-400">
                  {fmtDate(milestone.date)}
                </span>
              )}
            </div>
            <h3 className="font-display text-base sm:text-lg text-white uppercase leading-tight">
              {milestone.title}
            </h3>
          </div>
        </div>

        {milestone.description && (
          <p className="text-sm text-gray-300 leading-snug mt-1">{milestone.description}</p>
        )}

        {/* Awards bullets */}
        {awards.length > 0 && (
          <ul className="mt-3 flex flex-wrap gap-1.5">
            {awards.map((a, i) => (
              <li
                key={i}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest bg-amber-400/10 ring-1 ring-amber-300/30 text-amber-200"
              >
                <Trophy className="w-3 h-3" /> {a}
              </li>
            ))}
          </ul>
        )}

        {/* Stats grid — support BOTH shapes we've seen in the wild:
              • plain object → { RBI: 129, AVG: '.312' }
              • array of { label/name/stat, value/val/amount } objects
                → [{ label:'RBI', value:129 }, ...]
            The admin editor persists the array shape but earlier tools
            wrote raw objects — normalise both here so the timeline
            never shows "[object Object] / 0". */}
        {(() => {
          if (!stats) return null;
          let pairs = [];
          if (Array.isArray(stats)) {
            pairs = stats
              .map((s) => {
                if (s == null) return null;
                if (typeof s === 'string') return [s, ''];
                const label = s.label ?? s.name ?? s.stat ?? s.key ?? '';
                const value = s.value ?? s.val ?? s.amount ?? s.number ?? s.count ?? '';
                return label || value ? [String(label), String(value)] : null;
              })
              .filter(Boolean);
          } else if (typeof stats === 'object') {
            pairs = Object.entries(stats).map(([k, v]) => [String(k), String(v)]);
          }
          if (!pairs.length) return null;
          return (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {pairs.map(([label, value], i) => (
                <div key={`${label}-${i}`} className="rounded-lg bg-black/50 ring-1 ring-white/10 px-2.5 py-1.5">
                  <p className="font-display text-sm text-white leading-tight">{value || '—'}</p>
                  <p className="text-[9px] uppercase tracking-widest text-gray-400 mt-0.5">
                    {label.replace(/_/g, ' ')}
                  </p>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Photos strip */}
        {photos.length > 0 && (
          <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
            {photos.map((src, i) => (
              <img
                key={i}
                src={src}
                alt=""
                className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0 rounded-lg object-cover ring-1 ring-white/10"
                loading="lazy"
              />
            ))}
          </div>
        )}

        {/* Videos */}
        {videos.length > 0 && (
          <div className="mt-3 space-y-2">
            {videos.map((v, i) => (
              <a
                key={i}
                href={v}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs text-blue-300 hover:text-blue-200 underline underline-offset-2"
              >
                ▶ Watch clip {videos.length > 1 ? i + 1 : ''}
              </a>
            ))}
          </div>
        )}

        {/* Articles */}
        {articles.length > 0 && (
          <ul className="mt-3 space-y-1">
            {articles.map((a, i) => (
              <li key={i}>
                <a
                  href={a.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-300 hover:text-white underline underline-offset-2"
                >
                  {a.outlet ? <span className="text-gray-500 mr-1">{a.outlet}:</span> : null}
                  {a.title || a.url}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

const RichCareerTimeline = ({ athlete }) => {
  const milestones = orderMilestones(athlete?.career_milestones || []);
  if (!milestones.length) return null;
  return (
    <section
      className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12"
      aria-label="Career timeline"
      data-testid="career-timeline-rich"
    >
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.28em] text-blue-300 mb-1">The Journey</p>
        <h2 className="font-display text-2xl sm:text-3xl uppercase text-white">Career Timeline</h2>
      </div>
      {/* Vertical rail */}
      <div className="relative">
        <span
          className="absolute top-2 bottom-2 w-[2px] rounded-full"
          style={{
            left: '15px',
            background: 'linear-gradient(180deg, transparent 0%, rgba(107,184,240,0.55) 12%, rgba(107,184,240,0.55) 88%, transparent 100%)',
          }}
          aria-hidden
        />
        <div className="hidden sm:block">
          <span
            className="absolute top-2 bottom-2 w-[2px] rounded-full"
            style={{
              left: '23px',
              background: 'linear-gradient(180deg, transparent 0%, rgba(107,184,240,0.55) 12%, rgba(107,184,240,0.55) 88%, transparent 100%)',
            }}
            aria-hidden
          />
        </div>
        {milestones.map((m) => (
          <MilestoneCard key={m.id || `${m.title}-${m.date}`} milestone={m} />
        ))}
      </div>
    </section>
  );
};

export default RichCareerTimeline;
